
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import EventOverviewChart from "@/components/analytics/EventOverviewChart";
import TimeseriesChart from "@/components/analytics/TimeseriesChart";
import StatsListCard from "@/components/analytics/StatsListCard";

// Define types needed (previously imported from useAnalyticsData)
interface EventTypeStat {
  name: string;
  count: number;
}

interface PageEventStat {
  page: string;
  views: number;
}

interface TimeseriesData {
  date: string;
  views: number;
  clicks: number;
}

const Analytics = () => {
  const [pageViewStats, setPageViewStats] = useState<PageEventStat[]>([
    { page: "/", views: 1250 },
    { page: "/articles/faang-ml-interviews", views: 840 },
    { page: "/learn", views: 633 },
    { page: "/articles/candidate-generation", views: 427 },
    { page: "/articles/harmful-content-detection", views: 315 }
  ]);
  
  const [buttonClickStats, setButtonClickStats] = useState<PageEventStat[]>([
    { page: "Sign Up", views: 386 },
    { page: "Get Premium", views: 245 },
    { page: "Book Coaching", views: 187 },
    { page: "Learn More", views: 135 },
    { page: "Contact Us", views: 92 }
  ]);
  
  const [eventTypeStats, setEventTypeStats] = useState<EventTypeStat[]>([
    { name: "page_view", count: 3465 },
    { name: "button_click", count: 1045 },
    { name: "form_submission", count: 256 },
    { name: "conversion", count: 87 }
  ]);
  
  const [timeseriesData, setTimeseriesData] = useState<TimeseriesData[]>([
    { date: "2025-05-08", views: 420, clicks: 145 },
    { date: "2025-05-09", views: 380, clicks: 132 },
    { date: "2025-05-10", views: 430, clicks: 154 },
    { date: "2025-05-11", views: 510, clicks: 165 },
    { date: "2025-05-12", views: 470, clicks: 148 },
    { date: "2025-05-13", views: 520, clicks: 172 },
    { date: "2025-05-14", views: 570, clicks: 189 }
  ]);
  
  const [isLoading, setIsLoading] = useState(false);
  
  const fetchAnalyticsData = () => {
    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      // Data is already set in state
      setIsLoading(false);
    }, 800);
  };
  
  useEffect(() => {
    fetchAnalyticsData();
    
    // Refresh data every 5 minutes
    const intervalId = setInterval(fetchAnalyticsData, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow py-8">
        <div className="container">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
            <Button onClick={fetchAnalyticsData} disabled={isLoading}>
              {isLoading ? "Loading..." : "Refresh Data"}
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <EventOverviewChart eventTypeStats={eventTypeStats} />
            <TimeseriesChart timeseriesData={timeseriesData} />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <StatsListCard 
              stats={pageViewStats}
              title="Top Pages"
              description="Most viewed pages"
              emptyMessage="No page view data available"
              statLabel="views"
            />
            
            <StatsListCard 
              stats={buttonClickStats}
              title="Top Button Clicks"
              description="Most clicked buttons"
              emptyMessage="No button click data available"
              statLabel="clicks"
            />
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Analytics;
